import { useEffect, useRef, useState } from "react";

export const prefersReduce = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Hydration-safe reduced-motion preference.
 *
 * The prerenderer has no media query, so render `false` on both server and the
 * client's first pass, then reconcile after hydration. Reading matchMedia
 * directly during render produced different landing markup for reduced-motion
 * visitors and was reported by Sentry as a hydration error.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduce(query.matches);
    sync();
    query.addEventListener?.("change", sync);
    return () => query.removeEventListener?.("change", sync);
  }, []);

  return reduce;
}

// Text ink to place ON the accent fill (dark for bright accents, white for deep).
export function pickInk(hex: string): string {
  try {
    const c = hex.replace("#", "");
    const h = c.length === 3 ? c.split("").map((x) => x + x).join("") : c;
    const r = parseInt(h.slice(0, 2), 16) / 255, g = parseInt(h.slice(2, 4), 16) / 255, b = parseInt(h.slice(4, 6), 16) / 255;
    const f = (x: number) => (x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4));
    const L = 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
    return L > 0.42 ? "#0b0d06" : "#ffffff";
  } catch { return "#0b0d06"; }
}

// Adds `.is-in` to every [data-reveal] as it scrolls into view (or immediately
// when motion is off / reduced). Call once in the page root.
export function useRevealObserver(motion: boolean) {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
    if (!motion || prefersReduce()) { els.forEach((el) => el.classList.add("is-in")); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: "0px 0px -6% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [motion]);
}

// Ref-based in-view flag for component-local triggers (counters, guard resolve).
export function useInView<T extends HTMLElement>(threshold = 0.4): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    if (prefersReduce()) { setInView(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e?.isIntersecting) { setInView(true); io.disconnect(); } }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

export function useCounter(to: number, active: boolean, dur = 1100): number {
  const reduce = usePrefersReducedMotion();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active || reduce) { setN(to); return; }
    let raf = 0, start = 0;
    const step = (t: number) => { if (!start) start = t; const p = Math.min(1, (t - start) / dur); setN(Math.round((1 - Math.pow(1 - p, 3)) * to)); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, to, dur, reduce]);
  return n;
}
